import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DragIndicator, Visibility, VisibilityOff } from '@mui/icons-material';
import { ReactSortable } from 'react-sortablejs';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import type { HostRoom } from '../../models/Room';

export interface RoomPublicationManagerProps {
  spaceId: string;
  rooms: HostRoom[];
  onRoomsChange: (rooms: HostRoom[]) => void;
  onInaccessible?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  isMobile?: boolean;
  onRoomClick: (roomId: string) => void;
}

export type RoomPublicationManagerHandle = {
  isDirty: () => boolean;
  save: () => Promise<void>;
  discard: () => void;
};

export function orderHostRooms(rooms: HostRoom[]): HostRoom[] {
  return [...rooms].sort((left, right) =>
    left.publicDisplayOrder - right.publicDisplayOrder
      || left.name.localeCompare(right.name)
      || left.id.localeCompare(right.id));
}

/** True when draft order or visibility differs from the last saved baseline. */
export function isPublicationDraftDirty(draft: HostRoom[], baseline: HostRoom[]): boolean {
  if (draft.length !== baseline.length) return true;
  if (draft.some((room, index) => room.id !== baseline[index]?.id)) return true;
  return draft.some((room) => {
    const previous = baseline.find((item) => item.id === room.id);
    return !previous || previous.visible !== room.visible;
  });
}

const RoomPublicationManager = forwardRef<RoomPublicationManagerHandle, RoomPublicationManagerProps>(({
  spaceId,
  rooms,
  onRoomsChange,
  onInaccessible,
  onDirtyChange,
  onRoomClick,
}, ref) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<HostRoom[]>(() => orderHostRooms(rooms));
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const draftRef = useRef(draft);
  const baselineRef = useRef<HostRoom[]>(orderHostRooms(rooms));
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  draftRef.current = draft;

  useEffect(() => {
    if (!dirty) {
      const ordered = orderHostRooms(rooms);
      setDraft(ordered);
      baselineRef.current = ordered;
    }
  }, [rooms, dirty]);

  const setDirtyState = (next: boolean) => {
    dirtyRef.current = next;
    setDirty(next);
    onDirtyChange?.(next);
  };

  const commitDraft = (next: HostRoom[]) => {
    setDraft(next);
    draftRef.current = next;
    setDirtyState(isPublicationDraftDirty(next, baselineRef.current));
    setMessage(null);
  };

  const commitOrderDraft = (next: HostRoom[]) => {
    commitDraft(next.map((room, index) => ({ ...room, publicDisplayOrder: index })));
  };

  const handleReorder = (newList: HostRoom[]) => {
    const ordered = newList
      .map((item) => draftRef.current.find((room) => room.id === item.id))
      .filter((room): room is HostRoom => Boolean(room));
    if (ordered.length !== draftRef.current.length) return;
    const orderChanged = ordered.some((room, index) => room.id !== draftRef.current[index]?.id);
    if (orderChanged) commitOrderDraft(ordered);
  };

  const setVisible = (roomId: string, visible: boolean) => {
    const next = draftRef.current.map((item) =>
      item.id === roomId ? { ...item, visible } : item);
    commitDraft(next);
  };

  const reloadRooms = async () => {
    const latest = orderHostRooms(await hostApiClient.getRooms(spaceId));
    setDraft(latest);
    draftRef.current = latest;
    baselineRef.current = latest;
    onRoomsChange(latest);
    setDirtyState(false);
  };

  const discard = () => {
    const baseline = orderHostRooms(rooms);
    setDraft(baseline);
    draftRef.current = baseline;
    baselineRef.current = baseline;
    setDirtyState(false);
    setMessage(null);
  };

  const save = async () => {
    if (!dirtyRef.current) return;
    const payload = draftRef.current;
    const baseline = baselineRef.current;
    const orderChanged = payload.some((room, index) => room.id !== baseline[index]?.id);
    const visibilityChanges = payload.filter((room) => {
      const previous = baseline.find((item) => item.id === room.id);
      return Boolean(previous && previous.visible !== room.visible);
    });
    setMessage(null);
    try {
      if (orderChanged) {
        await hostApiClient.reorderRooms(spaceId, {
          roomIds: payload.map((room) => room.id),
        });
      }
      for (const room of visibilityChanges) {
        await hostApiClient.updateRoomVisibility(room.id, { visible: room.visible });
      }
      onRoomsChange(payload);
      baselineRef.current = payload;
      setDirtyState(false);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        try {
          await reloadRooms();
          setMessage({
            severity: 'error',
            text: t('rooms.publicationStale', 'The room list changed. It was reloaded; review it and try again.'),
          });
        } catch {
          setMessage({ severity: 'error', text: t('rooms.publicationReloadFailed', 'Saving failed and the room list could not be reloaded.') });
        }
      } else if (status === 404) {
        setMessage({ severity: 'error', text: t('rooms.publicationNotFound', 'This space is no longer available.') });
        onInaccessible?.();
      } else {
        setMessage({
          severity: 'error',
          text: t('rooms.publicationSaveFailed', 'Could not save room order or visibility.'),
        });
      }
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({
    isDirty: () => dirtyRef.current,
    save,
    discard,
  }), [rooms, spaceId, onRoomsChange, onInaccessible, t]);

  if (draft.length === 0) {
    return (
      <Box
        sx={{
          mt: 2,
          mb: 2,
          textAlign: 'center',
          py: 4,
          bgcolor: 'background.default',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('space.noRooms', 'No rooms yet')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: dirty ? 'primary.main' : 'divider',
          overflow: 'hidden',
          bgcolor: 'rgba(255,255,255,0.02)',
        }}
      >
        <ReactSortable
          list={draft}
          setList={handleReorder}
          animation={200}
          handle=".room-drag-handle"
        >
          {draft.map((room) => {
            const hidden = !room.visible;
            const visibilityLabel = room.visible
              ? t('rooms.hideRoom', 'Hide from public booking')
              : t('rooms.showRoom', 'Show on public booking');

            return (
              <Box
                key={room.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: { xs: 1.25, sm: 1.5 },
                  py: 1.25,
                  opacity: hidden ? 0.72 : 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  transition: 'background-color 0.15s ease, opacity 0.15s ease',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.04)',
                  },
                }}
              >
                <IconButton
                  className="room-drag-handle"
                  size="small"
                  aria-label={t('rooms.dragToReorder', 'Drag to reorder')}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    cursor: 'grab',
                    color: 'text.disabled',
                    flexShrink: 0,
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <DragIndicator fontSize="small" />
                </IconButton>

                <Box
                  onClick={() => onRoomClick(room.id)}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    cursor: 'pointer',
                    py: 0.25,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    noWrap
                    sx={{ letterSpacing: '-0.01em' }}
                  >
                    {room.name}
                  </Typography>
                </Box>

                <Tooltip title={visibilityLabel}>
                  <span>
                    <IconButton
                      size="small"
                      color={room.visible ? 'primary' : 'default'}
                      aria-label={`${t('rooms.publicVisibilityFor', 'Public visibility for')} ${room.name}`}
                      aria-pressed={room.visible}
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisible(room.id, !room.visible);
                      }}
                      sx={{
                        color: room.visible ? 'primary.main' : 'text.disabled',
                        flexShrink: 0,
                      }}
                    >
                      {room.visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </ReactSortable>
      </Box>
    </Stack>
  );
});

RoomPublicationManager.displayName = 'RoomPublicationManager';

export default RoomPublicationManager;
